import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useDataService } from '../hooks/useDataService';
import AuthenticatedImage from './AuthenticatedImage';

/**
 * InformationAllPage Component
 * Displays all corporate information/news announcements with category filtering
 */

const InformationAllPageContainer = styled.div`
  background: #F9FAFA;
  min-height: 100vh;
  padding: 0 0 80px;

  @media (max-width: 768px) {
    padding: 0 20px 120px;
`;

const BreadcrumbSection = styled.div.attrs({className: 'breadcrumb-section'})`
  padding: 80px 0 40px;
`;

const BreadcrumbContainer = styled.div.attrs({className: 'breadcrumb-container'})`
  margin: 0 116px;
  @media (max-width: 768px) {
      margin: 0;
    }
`;

const BreadcrumbText = styled.p.attrs({className: 'breadcrumb-text'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 1;
  color: #9B9B9B;
  margin: 0;

  a {
    color: #9B9B9B;
    text-decoration: none;
    transition: color 0.3s ease;

    &:hover {
      color: #0F3F8E;
    }
  }
`;

const PageTitleSection = styled.section.attrs({className: 'page-title-section'})`
    margin: 0 116px;
    @media (max-width: 768px) {
      margin: 0;
    }
`;

const PageTitleMain = styled.h1.attrs({className: 'page-title'})`
  font-family: 'A P-OTF Aoto Gothic StdN', 'Noto Sans JP', sans-serif;
  font-weight: 700;
  font-size: 56px;
  line-height: 1;
  letter-spacing: 0.05em;
  color: #333333;
  margin: 0 0 16px 0;

  @media (max-width: 768px) {
    font-size: 44px;
    line-height: 1.5;
    margin: 0 0 12px 0;
  }
`;

const PageTitleSub = styled.h2.attrs({className: 'page-subtitle'})`
  font-family: 'ITC Avant Garde Gothic Pro', 'Noto Sans JP', sans-serif;
  font-weight: 500;
  font-size: 18px;
  line-height: 1;
  letter-spacing: 0.05em;
  color: #9B9B9B;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const CategoryFilterContainer = styled.div.attrs({className: 'category-filter-container'})`
  position: sticky;
  top: 100px;
  height: 300px;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    position: relative;
    top: 0;
    height: auto;
  }
`;

const CategoryFilterTabs = styled.div.attrs({className: 'category-filter-tabs'})`
  display: flex;
  justify-content: center;
  flex-direction: column;
  gap: 16px;
  margin: 46px 0;

  @media (max-width: 768px) {
    gap: 30px;
    flex-direction: row;
    justify-content: left;
    flex-wrap: wrap;
  }
`;

const CategoryTab = styled.button.attrs({className: 'category-tab'})<{ $isActive: boolean }>`
  margin-left: 40px;
  display: flex;
  align-items: center;
  gap: 15px;
  background: none;
  border: none;
  cursor: pointer;
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 1.75em;
  letter-spacing: 0.05em;
  color: ${props => props.$isActive ? '#333333' : '#333333'};
  opacity: ${props => props.$isActive ? '1' : '0.2'};
  transition: opacity 0.3s ease;
  padding: 0;

  @media (max-width: 768px) {
    // width: 94px;
    height: 28px;
    margin-left: 0;
    justify-content: center;
  }

  &:hover {
    opacity: 1;
  }
    &:nth-child(3) {/*フロム人事は非表示*/
      display: none;
    }
`;

const CategoryTabIndicator = styled.div.attrs({className: 'category-tab-indicator'})<{ $color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.$color};
`;

const CategoryFilterDivider = styled.div.attrs({className: 'category-filter-divider'})`
  height: 1px;
  background: #E5E5E5;
  margin: 0 20px;

  @media (max-width: 768px) {
    margin: 0;
  }
`;

const NewsListSection = styled.section.attrs({className: 'news-list-section'})`
  max-width: 1280px;
  margin: 0 auto;
  padding-top: 80px;
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 156px;

  @media (max-width: 768px) {
    grid-template-columns: unset;
    gap: 60px;
  }
`;

const NewsListGrid = styled.div.attrs({className: 'news-list-grid'})`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  flex-direction: column;
  gap: 34px;

  @media (max-width: 768px) {
    gap: 30px;
    grid-template-columns: unset;
  }
`;

const NewsListMessage = styled.div.attrs({className: 'new-list-message'})`
  text-align: center;
  padding: 40px;
  grid-column: 2;

  @media (max-width: 768px) {
    grid-column: unset;
  }
`;

const NewsItem = styled(Link).attrs({className: 'news-item'})`
  flex: 1;
  display: flex;
  flex-direction: column;
  text-decoration: none;
  color: inherit;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-4px);
  }
`;

const NewsItemImage = styled.div.attrs({className: 'news-item-image'})`
  //width: 100%;
  //height: 200px;
  aspect-ratio: 4/3;
  border-radius: 10px;
  overflow: hidden;
  position: relative;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    // height: 180px;
    border-radius: 10px;
  }
`;

const NewsItemContent = styled.div.attrs({className: 'news-item-content'})`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const NewsItemMeta = styled.div.attrs({className: 'news-item-meta'})`
  display: flex;
  align-items: center;
  gap: 20px;

  @media (max-width: 768px) {
    align-items: flex-start;
    gap: 10px;
  }
`;

const NewsItemDate = styled.span.attrs({className: 'news-item-data'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 1.45em;
  color: #9B9B9B;
`;

const NewsItemCategory = styled.span.attrs({className: 'news-item-category'})<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 10px 1px;
  height: 20px;
  background: ${props => props.$color};
  border-radius: 10px;
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 700;
  font-size: 13px;
  line-height: 1.38em;
  letter-spacing: 0.05em;
  color: #FFFFFF;
  text-wrap-mode: nowrap;
`;

const NewsItemTitle = styled.h3.attrs({className: 'news-item-title'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 1.75em;
  letter-spacing: 0.05em;
  color: #333333;
  margin: 0;
`;



const LoadMoreButtonWrapper = styled.div.attrs({className: 'load-more-button-wrapper'})`
  text-align: center;
  grid-column: 2;

  @media (max-width: 768px) {
    grid-column: unset;
  }
`;

const InformationAllPage: React.FC = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Use data service hook for data management
  const {
    posts,
    loading,
    error,
    categories,
    categoriesLoading,
    hasMore,
    loadMorePosts,
    filterByCategory,
    activeCategory: wpActiveCategory,
    searchPosts,
    testConnection,
    dataSource
  } = useDataService(9); //posts per page


  const categoryNames = [ 'お知らせ', 'リリース', 'フロム人事'];

  // Handle category filter
  const handleCategoryFilter = (categoryId: number | null) => {
    filterByCategory(categoryId);
  };

  // Get category color
  const getCategoryColor = (categoryName: string) => {
    switch (categoryName.toLowerCase()) {
      case 'お知らせ':
      case 'news':
        return '#FDC22D';
      case 'リリース':
      case 'release':
        return '#92D382';
      case 'フロム人事':
      case 'fromrecruit':
        return '#FF9294';
      default:
        return '#FDC22D';
    }
  };

  return (
    <InformationAllPageContainer>
      {/* Breadcrumb Navigation */}
      <BreadcrumbSection>
        <BreadcrumbContainer>
          <BreadcrumbText>
            <Link to="/">トップ</Link> ー お知らせ
          </BreadcrumbText>
        </BreadcrumbContainer>
      </BreadcrumbSection>

      {/* Page Title Section */}
      <PageTitleSection>
          <PageTitleMain>お知らせ</PageTitleMain>
          <PageTitleSub>Information</PageTitleSub>
      </PageTitleSection>

      {/* Category Filter Section */}
      <NewsListSection>
        <CategoryFilterContainer>
          <CategoryFilterDivider />
          <CategoryFilterTabs>
            <CategoryTab 
              $isActive={wpActiveCategory === null}
              onClick={() => handleCategoryFilter(null)}
            >
              <CategoryTabIndicator $color="#333333" />
              新着
            </CategoryTab>
            {categories.map((category) => (
              categoryNames.includes(category.name) &&
              <CategoryTab 
                key={category.id}
                $isActive={wpActiveCategory === category.id}
                onClick={() => handleCategoryFilter(category.id)}
              >
                <CategoryTabIndicator $color={getCategoryColor(category.name)} />
                {category.name}
              </CategoryTab>
            ))}
          </CategoryFilterTabs>
          <CategoryFilterDivider />
        </CategoryFilterContainer>

      {/* News List Section */}
          <NewsListGrid>
            {
              loading ? <NewsListMessage>読み込み中...</NewsListMessage>
              : error ? <NewsListMessage>エラー: {error}</NewsListMessage>
              : posts.length === 0 ? <NewsListMessage>記事が見つかりません</NewsListMessage>
              : (
                  posts.map((post) => (
                      <NewsItem key={post.id} to={`/information/${post.id}`}>
                        <NewsItemImage>
                          {post._embedded?.['wp:featuredmedia']?.[0]?.source_url ? (
                            <AuthenticatedImage 
                              src={post._embedded['wp:featuredmedia'][0].source_url} 
                              alt={post._embedded['wp:featuredmedia'][0].alt_text || post.title.rendered}
                              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '10px' }}
                            />
                          ) : (
                            <div style={{ 
                              width: '100%', 
                              height: '100%', 
                              background: '#D9D9D9', 
                              borderRadius: '10px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#666'
                            }}>
                              画像なし
                            </div>
                          )}
                        </NewsItemImage>
                        <NewsItemContent>
                          <NewsItemMeta>
                            <NewsItemDate>
                              {new Date(post.date).toLocaleDateString('ja-JP', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit'
                              }).replace(/\//g, '.')}
                            </NewsItemDate>
                            {post._embedded?.['wp:term']?.[0]?.[0] && (
                              <NewsItemCategory $color={getCategoryColor(post._embedded['wp:term'][0][0].name)}>
                                {post._embedded['wp:term'][0][0].name}
                              </NewsItemCategory>
                            )}
                          </NewsItemMeta>
                          <NewsItemTitle 
                            dangerouslySetInnerHTML={{ __html: post.title.rendered }}
                          />
                        </NewsItemContent>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '10px',
                          background: '#F9FAFA',
                          border: 'none',
                          borderRadius: '26px',
                          padding: '8px 16px',
                          alignSelf: 'flex-end',
                          marginTop: 'auto'
                        }}>
                          <img src="/images/arrow-right.svg" alt="arrow right" style={{ width: '20px', height: '20px' }} />
                        </div>
                      </NewsItem>
                    )
                  )
            )}

            {/* Load More Button */}
            {hasMore && !loading && !error && (
                <LoadMoreButtonWrapper>
                <button 
                  onClick={loadMorePosts}
                  style={{
                    background: '#0F3F8E',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: '26px',
                    padding: '15px 30px',
                    fontSize: '16px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                >
                  もっと見る
                </button>
                </LoadMoreButtonWrapper>
              // </div>
            )}
          </NewsListGrid>
      </NewsListSection>
    </InformationAllPageContainer>
  );
};

export default InformationAllPage;
